import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Smartphone, Zap, Cloud, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

export function WelcomeScreen() {
  const navigate = useNavigate();
  const { trackWelcomeCompleted } = useInstallPrompt();
  const [currentStep, setCurrentStep] = useState(0);

  const features = [
    {
      icon: Smartphone,
      title: 'Native App Experience',
      description: 'Access BuildCRM directly from your home screen like a native app',
    },
    {
      icon: Cloud,
      title: 'Works Offline',
      description: 'View and edit data even without internet connection',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized performance with instant loading',
    },
    {
      icon: Bell,
      title: 'Push Notifications',
      description: 'Stay updated with real-time alerts for important events',
    },
  ];

  const handleComplete = async () => {
    await trackWelcomeCompleted();
    localStorage.setItem('pwa-welcome-completed', 'true');
    navigate('/');
  };

  const handleNext = () => {
    if (currentStep < features.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const CurrentIcon = features[currentStep].icon;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to BuildCRM!</CardTitle>
          <CardDescription>
            You've successfully installed the app. Let's show you what's new.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Feature showcase */}
          <div className="text-center space-y-4 py-8">
            <div className="flex justify-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                <CurrentIcon className="h-10 w-10 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold">{features[currentStep].title}</h3>
            <p className="text-muted-foreground">{features[currentStep].description}</p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2">
            {features.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-primary'
                    : 'w-2 bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSkip} className="flex-1">
              Skip
            </Button>
            <Button onClick={handleNext} className="flex-1">
              {currentStep < features.length - 1 ? 'Next' : 'Get Started'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
